import React from 'react';
import {
    ActionIcon,
    Badge,
    Group,
    Select,
    Stack,
    Text,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { Member } from '../../data/Member';
import { tenantRoleFrom } from './tenantRole';
import RolePicker from './RolePicker';

type Props = {
    member: Member;
    /** Assignable tenant role names for the Role picker. */
    assignableRoles: string[];
    /** Full permission catalog; grantable options are derived from this. */
    permissionCatalog: string[];
    busy?: boolean;
    onRoleChange: (role: string) => void;
    onGrantPermission: (permission: string) => void;
    onRevokePermission: (permission: string) => void;
};

/**
 * Presentational body for managing one org member's tenant role and direct
 * permissions. Callers (e.g. MembersPage Drawer) own data fetching and mutations.
 */
const ManageMemberPanel: React.FC<Props> = ({
    member,
    assignableRoles,
    permissionCatalog,
    busy = false,
    onRoleChange,
    onGrantPermission,
    onRevokePermission,
}) => {
    const tenantRole = tenantRoleFrom(member.roles);
    const grantable = permissionCatalog.filter(
        permission => !member.permissions.includes(permission),
    );

    return (
        <Stack gap="lg">
            <RolePicker
                roles={assignableRoles}
                value={tenantRole}
                onChange={onRoleChange}
                disabled={busy}
                name={`member-role-${member.id}`}
            />

            <Stack gap="xs">
                <Text fw={600}>Additional permissions</Text>
                <Text size="xs" c="dimmed">
                    Granted on top of this member’s role.
                </Text>
                <Group gap="xs">
                    {member.directPermissions.length === 0 && (
                        <Text size="sm" c="dimmed">
                            None
                        </Text>
                    )}
                    {member.directPermissions.map(permission => (
                        <Badge
                            key={permission}
                            variant="outline"
                            rightSection={
                                <ActionIcon
                                    size="xs"
                                    variant="transparent"
                                    color="gray"
                                    disabled={busy}
                                    onClick={() => onRevokePermission(permission)}
                                    aria-label={`Revoke ${permission}`}
                                >
                                    <IconX size={12} />
                                </ActionIcon>
                            }
                        >
                            {permission}
                        </Badge>
                    ))}
                </Group>
                <Select
                    placeholder="Grant a permission…"
                    searchable
                    disabled={busy || grantable.length === 0}
                    data={grantable}
                    value={null}
                    onChange={permission => permission && onGrantPermission(permission)}
                />
            </Stack>
        </Stack>
    );
};

export default ManageMemberPanel;
export type { Props as ManageMemberPanelProps };
